import type { ShoppingBagItem } from '@ori-events/basket-updated';
import { List, Typography, styled, ScopedCssBaseline } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import type { FC, MouseEvent } from 'react';
import { FormattedMessage } from 'react-intl';

import { Footer, Header, Item } from './content';
import Loader from './content/Loader';
import { translationKeys } from '~/models';
import { basePositionStyles } from '~/utils';

export const minibagWidthStyle = 'var(--minibag-desktop-width, 400px)';
interface MiniBagProps {
  breakpointIndex: number;
  isECatalogue: boolean;
  items: ShoppingBagItem[];
  isDataLoaded: boolean;
  isLoading: boolean;
  onMouseEnter: (event: MouseEvent<HTMLDivElement>) => void;
  onMouseLeave: (event: MouseEvent<HTMLDivElement>) => void;
}

const Root = styled('div')<{ ownerState: { breakpointIndex: number; isECatalogue: boolean } }>(
  ({ theme, ownerState }) => ({
    ...basePositionStyles,
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    left: 'auto',
    maxWidth: 'calc(100vw - 40px)',
    width:
      ownerState.isECatalogue && ownerState.breakpointIndex === 2 ? minibagWidthStyle : '400px',
    zIndex: 1,
  }),
);

const ItemWrapper = styled(ScopedCssBaseline)(({ theme }) => ({
  position: 'relative',
  display: 'block',
  boxSizing: 'border-box',
  overflow: 'hidden',
  flex: 1,
  background: theme.palette.background.paper,
}));

const EmptyBag = styled('div')(({ theme }) => ({
  position: 'inherit',
  top: '40%',
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const Items = styled(List)(({ theme }) => ({
  boxSizing: 'border-box',
  listStyleType: 'none',
  maxHeight: '100%',
  overflow: 'auto',
  paddingTop: theme.spacing(13),
  paddingBottom: theme.spacing(15),
  overscrollBehavior: 'contain',
}));

export const MiniBag: FC<MiniBagProps> = ({
  breakpointIndex,
  isECatalogue,
  items,
  isDataLoaded,
  isLoading,
  onMouseEnter,
  onMouseLeave,
}) => {
  const quantity = items.reduce((total, current) => total + current.requestedQuantity, 0);
  const { getTestId } = useTestId();
  const ownerState = { breakpointIndex, isECatalogue };

  return (
    <Root
      ownerState={ownerState}
      data-testid={getTestId('content')}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Header quantity={quantity} />
      {isLoading || !isDataLoaded ? <Loader /> : null}
      <ItemWrapper data-testid={getTestId('items')}>
        {items.length > 0 && (
          <Items>
            {items.map((item: ShoppingBagItem) => (
              <Item
                key={`minibag-item-${item.lineNumber}-${item.itemCode}-${item.itemType}-${item.totalPrice}`}
                {...item}
              />
            ))}
          </Items>
        )}
        {isDataLoaded && items.length === 0 ? (
          <EmptyBag data-testid={getTestId('empty')}>
            <Typography variant="h6">
              <FormattedMessage id={translationKeys.emptyBag} />
            </Typography>
            <Typography variant="body1">
              - <FormattedMessage id={translationKeys.emptyBagMessage} /> -
            </Typography>
          </EmptyBag>
        ) : null}
      </ItemWrapper>
      <Footer />
    </Root>
  );
};
